import { isDevEnv } from '@config/environment'
import { AuthStatus, useAuth } from '@features/auth'
import { AuroraNavButton } from '@features/misc/components/AuroraNavButton'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { PlanType } from 'api-validations/user-api'
import { Fragment } from 'react'
import { generatePath, Link, NavLink, useLocation } from 'react-router'
import {
  OrgLevelPermissions,
  useAccount,
  useIsDealerOrg,
  useOrgCodeOrUndefined,
  useOrgInfo,
  useRecentOrgCodes
} from 'shared-ui/active-operation'
import { ButtonKind, Icon, IconRotation, PanoramaButton } from 'shared-ui/components'
import { useDebug } from 'shared-ui/debug'
import { SpriteMap, SpriteMap16 } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { useLocalization } from 'shared-ui/localization'
import { useNotificationStats } from 'shared-ui/notifications'
import { isNonNil } from 'utils'

import { path } from '../routes/paths'
import { PanoramaLogoLink } from './PanoramaLogoLink'
import { SignInButton } from './SignInButton'
import { SignUpButton } from './SignUpButton'

const MAX_ITEMS_IN_DROPDOWN = 4

type LinkConfig = {
  label: string
  path: string
}

const NavBar: React.FC = () => {
  const account = useAccount()
  const { logError } = useDebug()

  const recentOrgCodes = useRecentOrgCodes()

  const { user, status, signOut, forceSignIn, signUp } = useAuth()
  const orgCode = useOrgCodeOrUndefined()
  const orgInfo = useOrgInfo()

  const isUserSignedIn = isNonNil(user)
  const isUserAppAdmin = account.data?.admin ?? false
  const isUserSupporter = account.data?.support ?? false
  const isInOrgScopedRoute = isNonNil(orgCode)
  const isDealerOrg = useIsDealerOrg()
  const { t } = useLocalization()

  const showAuroraButton = isInOrgScopedRoute && isUserSignedIn

  const navLinks: LinkConfig[] = []

  const orgScopedLinks = (orgCode: string): LinkConfig[] => [
    {
      label: t(($) => $.agConcepts.field, { count: 2 }),
      path: `/${orgCode}/map`
    },
    {
      label: t(($) => $.titles.manageOperation),
      path: `/${orgCode}/operation`
    },
    {
      label: t(($) => $.titles.reports),
      path: `/${orgCode}/reporting`
    }
  ]

  const dealerOrgLinks = (orgCode: string): LinkConfig[] => [
    {
      label: t(($) => $.dealer.fullList.operations),
      path: `/${orgCode}/dealer`
    },
    {
      label: t(($) => $.map.map),
      path: `/${orgCode}/map/dealer`
    },
    {
      label: t(($) => $.titles.manageDealership),
      path: `/${orgCode}/operation`
    }
  ]

  const adminOnlyLinks: LinkConfig[] = [
    {
      label: t(($) => $.titles.admin),
      path: '/admin'
    }
  ]

  const supportOnlyLinks: LinkConfig[] = [
    {
      label: t(($) => $.titles.support),
      path: path.supportOperations
    }
  ]

  if (isInOrgScopedRoute) {
    if (isDealerOrg) {
      navLinks.push(...dealerOrgLinks(orgCode))
    } else {
      navLinks.push(...orgScopedLinks(orgCode))
    }
  }

  if (isUserAppAdmin) {
    navLinks.push(...adminOnlyLinks)
  }

  if (isUserSupporter) {
    navLinks.push(...supportOnlyLinks)
  }

  const memberships =
    account.data?.orgMemberships?.filter(OrgLevelPermissions.hasNonSupporterRole) ?? []

  const recentMemberships = recentOrgCodes
    .map((recentOrgCode) => memberships.find((membership) => membership.orgCode === recentOrgCode))
    .filter(isNonNil)
  const orderedMemberships = [
    ...recentMemberships,
    ...memberships.filter((membership) => !recentOrgCodes.includes(membership.orgCode))
  ]
  const dealerMemberships = orderedMemberships.filter((m) => m.planType === PlanType.Dealer)
  const operationMemberships = orderedMemberships.filter((m) => m.planType !== PlanType.Dealer)
  const hasDealerOrgs = dealerMemberships.length > 0

  // Show dealer orgs first, then operations, up to MAX_ITEMS_IN_DROPDOWN total
  const dealerItemsToShow = dealerMemberships.slice(0, MAX_ITEMS_IN_DROPDOWN)
  const remainingSlots = MAX_ITEMS_IN_DROPDOWN - dealerItemsToShow.length
  const operationItemsToShow = operationMemberships.slice(0, remainingSlots)

  if (account.error) logError(account.error)

  const location = useLocation()

  return (
    <Fragment>
      <nav
        // Invisible in print, and taking no space either: hidden but still laid out, the bar left a
        // band across the top of every printed page. Its height and top border are inline, so both
        // have to be overridden rather than merely restyled. The logo prints on regardless — it is
        // positioned out of this flow by `logo-print-float`.
        className="print-color-exact sticky top-0 z-50 flex w-full flex-none flex-row items-center border-[rgb(255,179,1)] bg-black py-4 pl-6 pr-6 text-white print:invisible print:!h-0 print:!border-t-0 print:!py-0"
        style={{
          borderTopWidth: `${NAV_BAR_BORDER_TOP_WIDTH_PX}px`,
          height: `${NAV_BAR_HEIGHT_PX + NAV_BAR_BORDER_TOP_WIDTH_PX}px`
        }}
      >
        <PanoramaLogoLink />
        <div className="mx-6 hidden xl:flex">
          {navLinks.map((link) => (
            <PanoramaNavLink key={link.path} to={link.path}>
              {link.label}
            </PanoramaNavLink>
          ))}
        </div>
        {isUserSignedIn ? (
          <div
            className="ml-auto flex items-center gap-3 print:hidden"
            hidden={location.pathname.includes('EULA')}
          >
            {showAuroraButton && <AuroraNavButton />}
            <NotificationBell />
            <Menu as="div">
              {() => (
                <>
                  <div
                    className="fixed inset-0 z-0 hidden h-screen bg-black/60 ui-open:block"
                    onWheel={(e) => e.preventDefault()}
                  />

                  <MenuButton
                    className="pan-title5 flex items-center gap-2"
                    data-testid="navbar-profile-button"
                  >
                    <div className="hidden whitespace-nowrap text-right ui-open:text-green-300 xl:flex">
                      {orgInfo?.orgName}
                    </div>
                    <div className="text-right ui-open:text-green-300 xl:hidden">
                      {t(($) => $.buttons.menu)}
                    </div>

                    <Icon
                      icon={SpriteMap16.Chevron16}
                      className="size-3 rotate-180 text-gray-500 ui-open:rotate-0 ui-open:text-green-300"
                    />
                  </MenuButton>
                  <MenuItems
                    className="fixed inset-x-0 bottom-0 z-10 flex flex-col rounded-b-lg px-1 sm:inset-x-auto sm:bottom-auto sm:right-6 sm:w-80"
                    style={{
                      top: `${NAV_BAR_HEIGHT_PX + NAV_BAR_BORDER_TOP_WIDTH_PX + 4}px`,
                      '--menu-max-height': `calc(100dvh - ${NAV_BAR_HEIGHT_PX + NAV_BAR_BORDER_TOP_WIDTH_PX + 8}px)`
                    }}
                    modal={false}
                  >
                    <div className="relative z-10 h-full bg-transparent text-black sm:h-auto">
                      <div className="h-full overflow-y-auto overscroll-contain rounded-md bg-black p-4 sm:h-auto sm:max-h-[var(--menu-max-height)]">
                        <div className="mb-5 xl:hidden">
                          <div className={menuItemGroupClasses}>
                            {navLinks.map((link) => (
                              <MenuItem key={link.path}>
                                <Link
                                  className={menuItemLinkClasses({
                                    active: location.pathname.startsWith(link.path)
                                  })}
                                  to={link.path}
                                >
                                  {link.label}
                                </Link>
                              </MenuItem>
                            ))}
                          </div>
                        </div>
                        {hasDealerOrgs && (
                          <>
                            <p className="pan-body-bold mb-2 text-gray-300">
                              {t(($) => $.dealer.dealers)}
                            </p>
                            <div className={menuItemGroupClasses}>
                              {dealerItemsToShow.map((op) => (
                                <MenuItem key={op.orgCode}>
                                  <Link
                                    className={menuItemLinkClasses({
                                      active: op.orgCode === orgCode
                                    })}
                                    to={`/${op.orgCode}/dealer`}
                                  >
                                    {op.orgName}
                                  </Link>
                                </MenuItem>
                              ))}
                            </div>
                          </>
                        )}
                        {operationItemsToShow.length > 0 && (
                          <>
                            <p
                              className={`pan-body-bold mb-2 text-gray-300 ${hasDealerOrgs ? 'mt-4' : ''}`}
                            >
                              {t(($) => $.agConcepts.operation)}
                            </p>
                            <div className={menuItemGroupClasses}>
                              {operationItemsToShow.map((op) => (
                                <MenuItem key={op.orgCode}>
                                  <Link
                                    className={menuItemLinkClasses({
                                      active: op.orgCode === orgCode
                                    })}
                                    to={`/${op.orgCode}/home`}
                                  >
                                    {op.orgName}
                                  </Link>
                                </MenuItem>
                              ))}
                            </div>
                          </>
                        )}
                        {!hasDealerOrgs && operationItemsToShow.length === 0 && (
                          <>
                            <p className="pan-body-bold mb-2 text-gray-300">
                              {t(($) => $.agConcepts.operation)}
                            </p>
                            <div className={menuItemGroupClasses} />
                          </>
                        )}
                        <div className="mt-2">
                          <div className={menuItemGroupClasses}>
                            <MenuItem>
                              <Link
                                className={menuItemLinkClasses({ active: false })}
                                to="/account/operations"
                              >
                                {t(($) => $.buttons.viewAllOperations)}
                                <Icon
                                  icon={SpriteMap16.Chevron16}
                                  rotation={IconRotation.Deg90}
                                  className="size-2.5"
                                />
                              </Link>
                            </MenuItem>
                          </div>
                        </div>
                        <div className="mt-6 flex w-full gap-2">
                          <MenuItem>
                            <PanoramaButton
                              as={Link}
                              to="/create-operation"
                              kind={ButtonKind.Secondary}
                              className={menuActionButtonClasses}
                              icon={SpriteMap.AddCircleIcon}
                            >
                              {t(($) => $.buttons.createOperation)}
                            </PanoramaButton>
                          </MenuItem>
                          <MenuItem>
                            <PanoramaButton
                              kind={ButtonKind.Secondary}
                              className={menuActionButtonClasses}
                              icon={SpriteMap.UploadIcon}
                              iconRotation={IconRotation.Deg90}
                              onClick={signOut}
                            >
                              {t(($) => $.buttons.signOut)}
                            </PanoramaButton>
                          </MenuItem>
                        </div>
                      </div>
                    </div>
                  </MenuItems>
                </>
              )}
            </Menu>
          </div>
        ) : status !== AuthStatus.Unknown ? (
          <div className="ml-auto hidden flex-row gap-2 sm:flex">
            <SignInButton onClick={forceSignIn}>{t(($) => $.buttons.signIn)}</SignInButton>
            <SignUpButton onClick={signUp}>{t(($) => $.buttons.signUp)}</SignUpButton>
          </div>
        ) : null}
      </nav>
    </Fragment>
  )
}

export default NavBar

/* Nav Bar Styles */

/**
 * Magic number to keep the navbar from jumping around when switching between admin/support and operations.
 * Represents height of tallest nav bar element (profile icon - 40px) plus vertical Nav padding (32px total).
 */
export const NAV_BAR_HEIGHT_PX = 72
/**
 * Width of the border above the navbar signifying the dev environment
 */
export const NAV_BAR_BORDER_TOP_WIDTH_PX = isDevEnv() ? 4 : 0

/* Menu Styles */

const menuActionButtonClasses =
  'flex-1 bg-gray-800 text-gray-300 hover:bg-emerald-50 hover:text-black active:bg-emerald-50 active:text-black'

const menuItemGroupClasses = 'border max-h-[400px] overflow-y-auto border-black rounded-md'

const menuItemLinkClasses = createVariants({
  base: 'pan-body-bold p-3 pl-5 border-b bg-gray-800 hover:bg-emerald-50 hover:text-black focus:bg-white focus:text-black w-full text-left text-gray-300 border-black flex items-center justify-between',
  variants: {
    active: { true: 'bg-green-300 text-black', false: '' }
  }
})

const panoramaNavLinkClasses = createVariants({
  base: 'pan-title5 py-2 px-6 rounded-lg',
  variants: {
    isActive: {
      true: 'text-black bg-green-300',
      false: 'text-gray-300 font-medium hover:text-green-300'
    }
  }
})

const PanoramaNavLink = ({ to, children }: { to: string; children: string }) => (
  <NavLink to={to} className={({ isActive }) => panoramaNavLinkClasses({ isActive })}>
    {children}
  </NavLink>
)

const NotificationBell: React.FC = () => {
  const orgCode = useOrgCodeOrUndefined()
  const notificationStats = useNotificationStats()

  if (orgCode === undefined) return null

  const unreadCount = notificationStats.unread
  const MAX_BADGE_COUNT = 9

  return (
    <Link
      to={generatePath(path.notifications, { orgCode })}
      className="relative mr-3 flex items-center text-gray-300 hover:text-green-300"
      aria-label="Notifications"
    >
      <Icon icon={SpriteMap.BellIcon} className="size-7" />
      {unreadCount > 0 && (
        <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
          {unreadCount > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : unreadCount}
        </span>
      )}
    </Link>
  )
}
